// Organization-wide communications hub. Built on the polymorphic
// Communication model (communications table) — calls logged from the hub are
// plain Communication rows with type CALL, so they appear on contact
// timelines and fire the same contact-score recalculation as the contact
// Communications tab. Replaces the deleted standalone call_logs module.

import { CallOutcomeEnum } from './communication'

/** Channel types the hub type filter exposes ('none' sentinel = all types). */
export const HUB_COMMUNICATION_TYPES = [
  'CALL',
  'EMAIL',
  'MEETING',
  'SMS',
] as const
export type HubCommunicationType = (typeof HUB_COMMUNICATION_TYPES)[number]

export const HUB_TYPE_LABELS: Record<HubCommunicationType, string> = {
  CALL: 'Calls',
  EMAIL: 'Emails',
  MEETING: 'Meetings',
  SMS: 'SMS',
}

export type HubCallDirection = 'OUTBOUND' | 'INBOUND'

/**
 * Call outcomes the hub accepts — the Communication vocabulary
 * (CallOutcomeEnum), which includes MEETING_BOOKED for behavioral scoring.
 */
export type HubCallOutcome = `${CallOutcomeEnum}`
export const HUB_CALL_OUTCOMES = Object.values(
  CallOutcomeEnum,
) as HubCallOutcome[]

/** One list row covering all communication types. */
export interface CommunicationsHubRowDTO {
  id: string
  /** CommunicationType: CALL, EMAIL, MEETING, SMS, LINKEDIN, WHATSAPP, OTHER */
  type: string
  direction: HubCallDirection
  contactId: string | null
  /** Hydrated from Contact (undefined when no/deleted contact) */
  contactName?: string
  moduleType: string | null
  moduleId: string | null
  /** Human label for the parent record (contact/deal/entity name) */
  moduleLabel?: string
  subject: string | null
  /** Plain-text body/notes preview, truncated server-side */
  preview: string | null
  callOutcome: string | null
  /** Communication.duration (seconds) — calls only */
  durationSeconds: number | null
  /** When it happened: Communication.sentAt, falling back to createdAt */
  occurredAt: string
  createdById: string | null
  /** Hydrated from the creator User (undefined when unknown/deleted) */
  createdByName?: string
  /** Deal linkage for hub-logged calls (metadata.dealId, or moduleId when moduleType=DEAL) */
  dealId: string | null
  dealName?: string
  /** metadata.phoneNumber for hub-logged calls */
  phoneNumber: string | null
  /** Follow-up task created at log time (Communication.linkedTaskId) */
  linkedTaskId: string | null
  createdAt: string
  updatedAt: string
}

export interface CommunicationsHubListDTO {
  items: CommunicationsHubRowDTO[]
  total: number
}

/** KPI row for a period (the hub queries the current week). */
export interface CommunicationsHubStatsDTO {
  /** CALL communications in the period */
  calls: number
  /** Counts per callOutcome value (rows without an outcome are excluded) */
  callsByOutcome: Record<string, number>
  /** Sum of call durations (seconds) over the period */
  talkTimeSeconds: number
  /** Calls with callOutcome MEETING_BOOKED */
  meetingsBooked: number
  /** EMAIL communications in the period */
  emailsLogged: number
  /** MEETING communications in the period */
  meetingsLogged: number
}

/**
 * POST /communications/hub/log — generic create covering every communication
 * type. Field-for-field the same shape the contact tab's create path sends
 * (type, direction, subject, content, occurred time, call/meeting fields),
 * plus the hub anchor (contactId and/or dealId — at least one required) and
 * the hub-only extras (phoneNumber, follow-up task). Routed through
 * CommunicationService.create so timeline visibility and contact-score
 * recalculation behave exactly like the contact Communications tab.
 */
export interface LogCommunicationInput {
  /** Anchor: at least one of contactId/dealId is required */
  contactId?: string
  /** Stored in Communication.metadata.dealId (see hub service) */
  dealId?: string
  /** CommunicationTypeEnum value: EMAIL, CALL, MEETING, SMS, LINKEDIN, WHATSAPP, OTHER */
  type: string
  direction: HubCallDirection
  subject?: string
  body?: string
  summary?: string
  // Email fields
  fromEmail?: string
  toEmails?: string[]
  // Call fields (seconds)
  duration?: number
  callOutcome?: HubCallOutcome
  // Meeting fields (ISO datetimes)
  scheduledAt?: string
  scheduledEndAt?: string
  location?: string
  meetingUrl?: string
  /** ISO datetime; defaults to now (stored as Communication.sentAt) */
  occurredAt?: string
  /** Stored in Communication.metadata.phoneNumber (calls) */
  phoneNumber?: string
  /** Creates a Task assigned to the caller and links it via linkedTaskId */
  createFollowUpTask?: {
    title: string
    dueInDays: number
  }
}

/**
 * POST /communications/hub/log-call — legacy call-only create, kept as a thin
 * delegate onto the generic `log` path for older clients.
 */
export interface LogCallInput {
  /** Anchor: at least one of contactId/dealId is required */
  contactId?: string
  /** Stored in Communication.metadata.dealId (see hub service) */
  dealId?: string
  direction: HubCallDirection
  outcome: HubCallOutcome
  durationSeconds?: number
  notes?: string
  /** Stored in Communication.metadata.phoneNumber */
  phoneNumber?: string
  /** ISO datetime; defaults to now (stored as Communication.sentAt) */
  occurredAt?: string
  /** Creates a Task assigned to the caller and links it via linkedTaskId */
  createFollowUpTask?: {
    title: string
    dueInDays: number
  }
}

/** PATCH /communications/hub/calls/:id — type CALL rows only. */
export interface UpdateCallInput {
  direction?: HubCallDirection
  outcome?: HubCallOutcome
  durationSeconds?: number | null
  notes?: string | null
  phoneNumber?: string | null
  occurredAt?: string
}
